import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowDown, Check, Target } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CincoPorquesWizardProps {
  valores: {
    porque_1?: string;
    porque_2?: string;
    porque_3?: string;
    porque_4?: string;
    porque_5?: string;
  };
  onChange: (porques: CincoPorquesWizardProps['valores']) => void;
  onCausaRaizIdentificada?: (nivel: number) => void;
  readOnly?: boolean;
}

export const CincoPorquesWizard = ({
  valores,
  onChange,
  onCausaRaizIdentificada,
  readOnly = false
}: CincoPorquesWizardProps) => {
  const [causaRaizNivel, setCausaRaizNivel] = useState<number | null>(null);

  const porques = [
    { key: 'porque_1' as const, label: 'Por que o problema ocorreu?' },
    { key: 'porque_2' as const, label: 'Por que isso aconteceu?' },
    { key: 'porque_3' as const, label: 'Por que isso ocorreu?' },
    { key: 'porque_4' as const, label: 'Por que isso se deu?' },
    { key: 'porque_5' as const, label: 'Por que isso veio a ocorrer?' },
  ];

  const handleChange = (key: keyof typeof valores, value: string) => {
    onChange({ ...valores, [key]: value });
  };

  const handleMarcarCausaRaiz = (nivel: number) => {
    setCausaRaizNivel(nivel);
    onCausaRaizIdentificada?.(nivel);
  };

  const getVisibleLevels = () => {
    let visibleCount = 1; // Always show first level
    
    for (let i = 0; i < porques.length; i++) {
      const key = porques[i].key;
      if (valores[key] && valores[key].trim()) {
        visibleCount = Math.min(i + 2, 5); // Show next level if current is filled
      }
    }
    
    return visibleCount;
  };

  const visibleLevels = getVisibleLevels();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-5 w-5 text-primary" />
        <h4 className="font-semibold">Técnica dos 5 Porquês</h4>
        {causaRaizNivel && (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Causa raiz identificada no nível {causaRaizNivel}
          </Badge>
        )}
      </div>

      <div className="relative">
        {porques.map((porque, index) => {
          const nivel = index + 1;
          const isVisible = nivel <= visibleLevels || readOnly;
          const isFilled = valores[porque.key]?.trim();
          const isCausaRaiz = causaRaizNivel === nivel;
          const isLast = index === porques.length - 1;

          if (!isVisible) return null;

          return (
            <div key={porque.key} className="relative">
              {/* Connection line */}
              {index > 0 && (
                <div className="absolute left-4 -top-4 h-4 w-px bg-border" />
              )}

              <div
                className={cn(
                  'flex items-start gap-4 p-4 rounded-lg border transition-all',
                  isCausaRaiz && 'bg-green-50 border-green-300',
                  !isFilled && !isCausaRaiz && 'bg-muted/30'
                )}
              >
                {/* Level indicator */}
                <div
                  className={cn(
                    'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                    isCausaRaiz 
                      ? 'bg-green-500 text-white' 
                      : isFilled 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted text-muted-foreground'
                  )}
                >
                  {isCausaRaiz ? <Check className="h-4 w-4" /> : nivel}
                </div>

                {/* Content */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      {porque.label}
                    </label>
                    <span className="text-xs text-muted-foreground">
                      {nivel}/5
                    </span>
                  </div>

                  <Input
                    value={valores[porque.key] || ''}
                    onChange={(e) => handleChange(porque.key, e.target.value)}
                    disabled={readOnly}
                    placeholder={`Digite a resposta para o porquê ${nivel}...`}
                    className={cn(
                      'transition-all',
                      isCausaRaiz && 'border-green-300 focus:ring-green-500'
                    )}
                  />

                  {/* Action buttons */}
                  {isFilled && !readOnly && !isCausaRaiz && (
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => handleMarcarCausaRaiz(nivel)}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Causa raiz encontrada aqui
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Arrow to next level */}
              {!isLast && nivel < visibleLevels && (
                <div className="flex justify-center py-2">
                  <ArrowDown className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!readOnly && visibleLevels === 5 && !causaRaizNivel && valores.porque_5?.trim() && (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => handleMarcarCausaRaiz(5)}
          >
            <Target className="h-4 w-4 mr-2" />
            Concluir análise - Causa raiz no nível 5
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4">
        Continue perguntando "Por quê?" até identificar a causa raiz do problema.
        Nem sempre são necessários todos os 5 níveis.
      </p>
    </div>
  );
};

export default CincoPorquesWizard;
